import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'

import type { Language, SupportedMarketKey } from '@nxt-insurance/constants'

import { getFlowSessionCookieName } from '@/server/routers/flows/utils/cookies'
import { removeImpermanentSearchParams } from '@/utils/url'

import type { FlowStepPageConfig, FlowStepPageProps } from './types'
import { useFlowCoupon } from './useFlowCoupon'
import { useFlowStepData } from './useFlowStepData'

export const readFlowSessionId = (flowName: string) => {
  if (typeof document === 'undefined') {
    return undefined
  }
  const cookieName = getFlowSessionCookieName(flowName)
  return (
    document.cookie
      .split('; ')
      .find((cookie) => cookie.startsWith(`${cookieName}=`))
      ?.split('=')[1] ?? undefined
  )
}

const getSessionInfo = <
  TData extends {
    flowValues?: Record<string, unknown>
    isAuthenticated?: boolean
    stepFlags?: unknown
  },
>(
  stepData: TData | undefined
) => ({
  sessionFlowValues: stepData?.flowValues,
  isAuthenticated: stepData?.isAuthenticated === true,
  stepFlags: stepData?.stepFlags as TData['stepFlags'] | undefined,
})

export const useFlowQueryParams = () => {
  const { query } = useRouter()
  const whitelistedParams = useMemo(() => removeImpermanentSearchParams(query).toString(), [query])
  return { urlCoupon: query.coupon, whitelistedParams }
}

export const useFlowStepSession = ({
  config,
  pageProps,
  language,
  market,
  urlCoupon,
}: {
  config: FlowStepPageConfig
  pageProps: FlowStepPageProps
  language: Language
  market: SupportedMarketKey
  urlCoupon: unknown
}) => {
  const hasFlowSession = readFlowSessionId(config.flowName) !== undefined
  const [stepQueryEnabled, setStepQueryEnabled] = useState(hasFlowSession)

  const stepData = useFlowStepData({
    config,
    props: pageProps,
    language,
    market,
    enabled: stepQueryEnabled,
  })
  const sessionInfo = getSessionInfo(stepData.query.data)

  const couponState = useFlowCoupon({
    config,
    sessionFlowValues: sessionInfo.sessionFlowValues,
    isAuthenticated: sessionInfo.isAuthenticated,
    urlCoupon,
    hasFlowSession,
    language,
    market,
  })

  useEffect(() => {
    if (couponState.codeToValidate === null) {
      setStepQueryEnabled(true)
    }
  }, [couponState.codeToValidate])

  return { ...stepData, ...sessionInfo, ...couponState }
}
